/* eslint-disable import/no-cycle */
import React, { ReactNode, useCallback } from 'react';
import { Control } from 'react-hook-form';
import {
  ElementDescription,
  FunctionMapping,
  UISchemaLanguage,
  UISchemaScope,
} from './types';
import { flattenUISchema, validateTranslation } from './helper';
import { buildLabelFromArray } from '../utils/stringUtils';

export interface ToElementsMapping {
  elementDescriptions: ElementDescription[];
  mapping: FunctionMapping;
}

export interface ToElementsProps {
  control: Control;
  language: UISchemaLanguage;
  currentScope: UISchemaScope;
  namePrefix: string;
}

export default function useDynaFormFactory() {
  const toElements = useCallback(function toElements(
    { elementDescriptions, mapping }: ToElementsMapping,
    { control, language, currentScope, namePrefix }: ToElementsProps,
  ): ReactNode[] {
    return elementDescriptions
      .filter(
        (description) =>
          !description.scopes || description.scopes.includes(currentScope),
      )
      .map((description) => {
        const { component, key, translation, children, ...data } = description;

        if (!key) return null;

        const Component = mapping[component] || mapping.Fallback;
        const isInput = component.endsWith('Field') || component === 'Array';

        const prefixedKey = buildLabelFromArray({
          input: [namePrefix, key],
          separator: '.',
        });

        return (
          <Component
            inputKey={prefixedKey}
            translation={validateTranslation({ translation, key })}
            control={control}
            language={language}
            data={{
              ...data,
              mapping,
              currentScope,
              namePrefix,
              isInput,
            }}
            key={prefixedKey}
          >
            {!children
              ? null
              : toElements(
                  {
                    elementDescriptions: flattenUISchema(children),
                    mapping,
                  },
                  {
                    control,
                    language,
                    currentScope,
                    namePrefix,
                  },
                )}
          </Component>
        );
      });
  }, []);

  return { toElements };
}
